import { useState } from "react";
import { Cookie, Shield, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SITE_CONFIG } from "@/config/site";

const LAST_UPDATED = "May 2026";

const SectionHeading = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-4">{children}</h2>
);

const SubHeading = ({ children }) => (
  <h3 className="text-xl font-semibold text-white mt-8 mb-3">{children}</h3>
);

const CookieCategoryCard = ({ title, alwaysOn, description, examples, enabled, onToggle }) => (
  <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 mb-4">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div>
        <h4 className="text-lg font-semibold text-white flex items-center gap-2">
          {title}
          {alwaysOn && (
            <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded-full bg-emerald-500/20 text-emerald-400">
              Always Active
            </span>
          )}
        </h4>
      </div>
      {!alwaysOn && (
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          data-testid={`cookie-toggle-${title.toLowerCase().replace(/\s/g, '-')}`}
        />
      )}
    </div>
    <p className="text-gray-400 text-sm mb-3">{description}</p>
    <div className="text-gray-500 text-xs">
      <span className="font-medium text-gray-400">Examples:</span>
      <ul className="mt-2 space-y-1 list-disc list-inside ml-1">
        {examples.map((ex, i) => (
          <li key={i}>{ex}</li>
        ))}
      </ul>
    </div>
  </div>
);

export const CookiePreferencesPage = () => {
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  const handleAcceptAll = () => {
    setFunctional(true);
    setAnalytics(true);
    setMarketing(true);
  };
  const handleRejectAll = () => {
    setFunctional(false);
    setAnalytics(false);
    setMarketing(false);
  };
  const handleSave = () => {
    // TODO: Persist preferences to localStorage or backend
    // eslint-disable-next-line no-alert
    alert("Your cookie preferences have been saved.");
  };

  return (
    <div className="pt-32 pb-20 min-h-screen inner-page" data-testid="cookie-preferences-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          {/* <Badge className="section-badge mb-6">
            <Cookie className="w-3 h-3 text-violet-400" />
            <span>Cookie Preferences</span>
          </Badge> */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-20 mb-4">
            Cookie Preferences
          </h1>
          <p className="text-gray-400 text-sm">Last Updated: {LAST_UPDATED}</p>
        </div>

        {/* Intro */}
        <SubHeading>Your Privacy Matters</SubHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          At QVPN, we respect your privacy. This page explains how we use cookies and similar
          technologies, and gives you control over your preferences.
        </p>

        <SectionHeading>What Are Cookies?</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          Cookies are small text files stored on your device when you visit our website. They
          help us understand how you use our services, remember your preferences, and improve
          your experience.
        </p>

        <SectionHeading>Types of Cookies We Use</SectionHeading>

        <CookieCategoryCard
          title="Essential Cookies"
          alwaysOn
          description="These cookies are necessary for the website to function properly. They enable core features like secure login, account access, and payment processing. You cannot disable these cookies."
          examples={["Session management", "Security and authentication", "Load balancing"]}
        />

        <CookieCategoryCard
          title="Functional Cookies"
          description="These cookies remember your choices and preferences to provide enhanced features and personalization."
          examples={["Language preferences", "Region settings", "Remembered login details"]}
          enabled={functional}
          onToggle={setFunctional}
        />

        <CookieCategoryCard
          title="Analytics Cookies"
          description="These cookies help us understand how visitors interact with our website by collecting anonymous information about page visits, traffic sources, and user behavior."
          examples={["Page view statistics", "Feature usage patterns", "Error reporting"]}
          enabled={analytics}
          onToggle={setAnalytics}
        />

        <CookieCategoryCard
          title="Marketing Cookies"
          description="These cookies are used to deliver relevant advertisements and measure the effectiveness of our marketing campaigns."
          examples={["Ad personalization", "Campaign tracking", "Social media integration"]}
          enabled={marketing}
          onToggle={setMarketing}
        />

        {/* Manage preferences */}
        <div className="mt-10 p-6 rounded-2xl bg-white/[0.03] border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">Manage Your Preferences</h3>
          <div className="flex flex-wrap gap-3">
            <Button className="btn-gradient" onClick={handleAcceptAll} data-testid="accept-all-cookies">
              Accept All Cookies
            </Button>
            <Button className="btn-primary" onClick={handleSave} data-testid="save-cookie-preferences">
              Save Preferences
            </Button>
            <Button className="btn-secondary" onClick={handleRejectAll} data-testid="reject-non-essential">
              Reject Non-Essential
            </Button>
          </div>
        </div>

        <SectionHeading>How to Control Cookies in Your Browser</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          You can also manage cookies through your browser settings:
        </p>
        <ul className="text-gray-300 text-sm space-y-2 mb-4 list-disc list-inside">
          <li><span className="font-medium text-white">Chrome:</span> Settings → Privacy and Security → Cookies</li>
          <li><span className="font-medium text-white">Firefox:</span> Settings → Privacy &amp; Security → Cookies</li>
          <li><span className="font-medium text-white">Safari:</span> Preferences → Privacy → Cookies</li>
          <li><span className="font-medium text-white">Edge:</span> Settings → Cookies and Site Permissions</li>
        </ul>
        <p className="text-gray-400 text-sm italic">
          Note: Blocking certain cookies may affect your experience on our website.
        </p>

        <SectionHeading>Third Party Cookies</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          We may use services from third parties that set their own cookies. These include:
        </p>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden mb-4">
          <div className="grid grid-cols-3 border-b border-white/10">
            <div className="p-4 text-sm font-medium text-gray-300">Provider</div>
            <div className="p-4 text-sm font-medium text-gray-300">Purpose</div>
            <div className="p-4 text-sm font-medium text-gray-300">Privacy Policy</div>
          </div>
          <div className="grid grid-cols-3 border-b border-white/5">
            <div className="p-4 text-sm text-gray-300">Google Analytics</div>
            <div className="p-4 text-sm text-gray-400">Website analytics</div>
            <div className="p-4 text-sm">
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300">
                View
              </a>
            </div>
          </div>
          <div className="grid grid-cols-3">
            <div className="p-4 text-sm text-gray-300">Stripe</div>
            <div className="p-4 text-sm text-gray-400">Payment processing</div>
            <div className="p-4 text-sm">
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300">
                View
              </a>
            </div>
          </div>
        </div>

        <SectionHeading>Updates to This Policy</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          We may update this cookie policy from time to time. The "Last Updated" date at the top
          indicates when changes were last made.
        </p>

        <SectionHeading>Contact Us</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          If you have questions about our use of cookies, please contact us at:
        </p>
        <a
          href={`mailto:${SITE_CONFIG.contact.supportEmail}`}
          className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300"
        >
          <Mail className="w-4 h-4" />
          {SITE_CONFIG.contact.supportEmail}
        </a>
      </div>
    </div>
  );
};

export default CookiePreferencesPage;
